import { jsPDF } from "jspdf";

/**
 * Shared A4 vector-PDF engine.
 *
 * Extracted from the Sales lead-brief generator (`pages/sales/leads/leadPdf.ts`)
 * so the documentation "Download PDF" builder (`pages/documentation/docPdf.ts`)
 * and any future PDF surface draw on ONE engine instead of re-deriving page
 * geometry, the WinAnsi text sanitizer, the markdown→blocks parser, and the
 * paginating layout writer.
 *
 * Everything here is a TRUE VECTOR primitive (jsPDF text/draw) — never an
 * html2canvas raster. Crisp selectable text, tiny files, and it runs
 * identically in the Tauri desktop WebKit shell (no print-to-PDF dependency).
 *
 * The render *styling* (heading sizes, section ornaments, meters) stays in each
 * builder — only the shared mechanics live here, so the Sales PDF's output is
 * byte-identical after the migration (guarded by `leadPdf.test.ts`).
 */

export type RGB = readonly [number, number, number];

// ── Page geometry (mm) ────────────────────────────────────────────────────
export const PAGE_W = 210;
export const PAGE_H = 297;
export const MARGIN = 16;
export const CONTENT_W = PAGE_W - MARGIN * 2; // 178mm
export const BOTTOM = PAGE_H - MARGIN; // last usable baseline
export const PT_TO_MM = 0.352_777_8;

// ── Palette (sRGB hex baked to RGB, mirrors theme.ts brand roles) ──────────
export const INK: RGB = [26, 26, 26];
export const NAVY: RGB = [27, 38, 79]; // brand navy — headings
export const MUTED: RGB = [110, 116, 128];
export const FAINT: RGB = [150, 156, 166];
export const RULE: RGB = [223, 226, 231];
export const GOOD: RGB = [46, 125, 50];
export const WARN: RGB = [176, 104, 0];
export const BAD: RGB = [198, 40, 40];
export const TRACK: RGB = [232, 234, 238];

/**
 * Collapse a string to the subset jsPDF's built-in Helvetica (WinAnsi) can
 * render: smart quotes/dashes/ellipsis fold to ASCII and anything outside
 * Latin-1 (emoji, ✓/✗, arrows, CJK) is dropped — those would otherwise render
 * as tofu. Callers draw their own status dots/markers so glyphs are never a
 * dependency.
 */
export function sanitize(s: string | null | undefined): string {
  return (s ?? "")
    .replace(/[‘’‚‹›]/g, "'")
    .replace(/[“”„«»]/g, '"')
    .replace(/[–—−]/g, "-")
    .replace(/…/g, "...")
    .replace(/•/g, "-")
    .replace(/[^\t\n\x20-\x7E\xA0-\xFF]/g, "")
    .replace(/[ \t]+\n/g, "\n");
}

/** Strip markdown inline syntax to plain text, keeping link targets
 * ("label (https://…)") so a printed doc stays actionable on paper. */
export function stripInlineMarkdown(s: string): string {
  return s
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, label: string, url: string) =>
      /^https?:\/\//i.test(url) ? `${label} (${url})` : label,
    );
}

export type MdBlock =
  | { kind: "h"; level: number; text: string }
  | { kind: "p"; text: string }
  | { kind: "li"; text: string; ordinal: string }
  | { kind: "code"; text: string }
  | { kind: "hr" };

export interface MarkdownGrammar {
  /** Heading levels to recognize. Default `[1, 2, 3]` (the docs reader);
   * the Sales brief passes `[2, 3]`. */
  headingLevels?: number[];
  /** Recognize ``` fenced code blocks (else they flow as paragraph text,
   * the legacy Sales behaviour). Default `true`. */
  fences?: boolean;
  /** Recognize `---` / `***` / `___` as a horizontal rule. Default `true`. */
  rules?: boolean;
}

/**
 * Lightweight markdown → flat blocks (headings, paragraphs, list items, and
 * optionally fenced code + rules). Mirrors the on-screen `Markdown` reader's
 * grammar so a PDF reads the same; output is plain blocks a layout engine can
 * paginate line-by-line.
 *
 * `grammar` lets a caller narrow the recognized syntax — the Sales brief opts
 * out of fences/rules and limits headings to `##`/`###` so its output is
 * unchanged from the pre-extraction parser.
 */
export function parseMarkdownBlocks(src: string, grammar: MarkdownGrammar = {}): MdBlock[] {
  const headingLevels = grammar.headingLevels ?? [1, 2, 3];
  const fences = grammar.fences ?? true;
  const rules = grammar.rules ?? true;
  const minH = Math.min(...headingLevels);
  const maxH = Math.max(...headingLevels);
  const headingRe = new RegExp(`^(#{${minH},${maxH}})\\s+(.+)$`);
  const ruleRe = /^(\*\*\*|---|___)$/;
  const ulRe = /^\s*[-*]\s+/;
  const olRe = /^\s*\d+\.\s+/;

  const lines = sanitize(src).replace(/\r\n/g, "\n").split("\n");
  const out: MdBlock[] = [];
  const isBreak = (line: string): boolean =>
    line.trim() === "" ||
    headingRe.test(line) ||
    ulRe.test(line) ||
    olRe.test(line) ||
    (fences && line.trimStart().startsWith("```")) ||
    (rules && ruleRe.test(line.trim()));

  let i = 0;
  while (i < lines.length) {
    const line = (lines[i] ?? "").trimEnd();
    if (line.trim() === "") { i++; continue; }

    if (fences && line.trimStart().startsWith("```")) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? "").trimStart().startsWith("```")) {
        buf.push(lines[i] ?? "");
        i++;
      }
      i++; // consume closing fence
      out.push({ kind: "code", text: buf.join("\n") });
      continue;
    }

    if (rules && ruleRe.test(line.trim())) {
      out.push({ kind: "hr" });
      i++;
      continue;
    }

    const hm = line.match(headingRe);
    if (hm && hm[1] && hm[2]) {
      out.push({ kind: "h", level: hm[1].length, text: stripInlineMarkdown(hm[2].trim()) });
      i++;
      continue;
    }

    if (ulRe.test(line)) {
      while (i < lines.length && ulRe.test(lines[i] ?? "")) {
        out.push({ kind: "li", ordinal: "-", text: stripInlineMarkdown((lines[i] ?? "").replace(ulRe, "").trim()) });
        i++;
      }
      continue;
    }

    if (olRe.test(line)) {
      let n = 1;
      while (i < lines.length && olRe.test(lines[i] ?? "")) {
        out.push({ kind: "li", ordinal: `${n}.`, text: stripInlineMarkdown((lines[i] ?? "").replace(olRe, "").trim()) });
        i++; n++;
      }
      continue;
    }

    const buf = [line];
    i++;
    while (i < lines.length) {
      const next = (lines[i] ?? "").trimEnd();
      if (isBreak(next)) break;
      buf.push(next);
      i++;
    }
    out.push({ kind: "p", text: stripInlineMarkdown(buf.join(" ")) });
  }
  return out;
}

/** The mm advance for one line at a given point size (1.35× leading). */
export function lineHeight(pt: number): number {
  return pt * PT_TO_MM * 1.35;
}

export interface WriteOpts {
  x?: number;
  width?: number;
  size?: number;
  style?: "normal" | "bold" | "italic";
  color?: RGB;
}

/**
 * Paginating layout writer over a jsPDF instance. Owns the vertical cursor
 * (`y`) and exposes the primitives every builder shares: wrapped text that
 * flows across pages (a line is never split across a page boundary), a
 * room-check + page-break helper, a vertical gap, a section header, and the
 * page-footer pass. The jsPDF instance is exposed as `.doc` so a builder can
 * draw its own bespoke ornaments (meters, dots, embedded images).
 */
export class PdfWriter {
  readonly doc: jsPDF;
  y: number;

  constructor(doc: jsPDF) {
    this.doc = doc;
    this.y = MARGIN;
  }

  setColor(c: RGB): void {
    this.doc.setTextColor(c[0], c[1], c[2]);
  }

  /** Ensure `h` mm of vertical room remains; otherwise start a new page. */
  need(h: number): void {
    if (this.y + h > BOTTOM) {
      this.doc.addPage();
      this.y = MARGIN;
    }
  }

  gap(mm: number): void {
    this.y += mm;
  }

  /** Write wrapped text, flowing across pages line-by-line. Advances `y`. */
  write(text: string, opts: WriteOpts = {}): void {
    const x = opts.x ?? MARGIN;
    const width = opts.width ?? CONTENT_W;
    const size = opts.size ?? 10;
    this.doc.setFont("helvetica", opts.style ?? "normal");
    this.doc.setFontSize(size);
    this.setColor(opts.color ?? INK);
    const lh = lineHeight(size);
    const lines = this.doc.splitTextToSize(sanitize(text), width) as string[];
    for (const ln of lines) {
      this.need(lh);
      this.y += lh;
      this.doc.text(ln, x, this.y);
    }
  }

  /** A section header (small-caps style) preceded by a hairline rule, kept
   * with at least the first body lines so a header never orphans at page end. */
  section(title: string): void {
    this.gap(4);
    this.need(lineHeight(10.5) + 16);
    this.doc.setDrawColor(RULE[0], RULE[1], RULE[2]);
    this.doc.setLineWidth(0.2);
    this.doc.line(MARGIN, this.y, MARGIN + CONTENT_W, this.y);
    this.gap(4.5);
    this.write(title.toUpperCase(), { size: 10.5, style: "bold", color: NAVY });
    this.gap(1.5);
  }

  /** "label … Page X of Y" footer on every page. Call last. */
  addPageFooters(label: string): void {
    const pages = this.doc.getNumberOfPages();
    for (let p = 1; p <= pages; p++) {
      this.doc.setPage(p);
      this.doc.setFont("helvetica", "normal");
      this.doc.setFontSize(8);
      this.setColor(FAINT);
      this.doc.text(sanitize(label), MARGIN, PAGE_H - 8, { maxWidth: CONTENT_W - 30 });
      const pageLabel = `Page ${p} of ${pages}`;
      this.doc.text(pageLabel, MARGIN + CONTENT_W - this.doc.getTextWidth(pageLabel), PAGE_H - 8);
    }
  }
}

/** Fresh A4 portrait document, compressed, mm units. */
export function newA4Doc(): jsPDF {
  return new jsPDF({ unit: "mm", format: "a4", compress: true });
}
